import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getCallsignDirectory, type CallsignDirectoryEntry, type CallsignEntity } from '@/lib/adminApi';

const ENTITY_LABEL: Record<CallsignEntity, string> = {
  company: 'Company',
  production: 'Production',
  site: 'Site',
  model: 'Model',
};

const ENTITY_PAGE: Partial<Record<CallsignEntity, string>> = {
  company: '/settings/companies',
  production: '/settings/groups',
  site: '/settings/groups',
};

interface CallsignsDirectoryProps {
  /** Models live on this same page: switch to the Model tab instead of navigating. */
  onShowModels: () => void;
}

/**
 * Settings → Metadata → Callsigns (universal search P1). A read-only
 * directory: callsigns are stored and edited on their own records.
 */
export function CallsignsDirectory({ onShowModels }: CallsignsDirectoryProps) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<CallsignDirectoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCallsignDirectory()
      .then((r) => { if (!cancelled) setEntries(r.entries); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'unknown error'); });
    return () => { cancelled = true; };
  }, []);

  const open = (e: CallsignDirectoryEntry) => {
    if (e.entity === 'model') onShowModels();
    else navigate(ENTITY_PAGE[e.entity] ?? '/settings');
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Shorthand people can search by. Each callsign is edited on the record itself — its company,
        production, site or model form. This list only shows where each one lives.
      </p>

      {error ? (
        <div role="alert" className="rounded-md border border-border p-4 text-sm text-[#E0556C]">
          Could not load callsigns: {error}
        </div>
      ) : entries === null ? (
        <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-md border border-border p-6 text-center text-sm text-muted-foreground">
          No callsigns yet. Add one on a company, production, site or model.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">Callsign</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Belongs to</th>
                <th className="px-3 py-2 font-medium">Company</th>
                <th className="px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((e) => (
                <tr key={`${e.entity}:${e.id}:${e.callsign}`}>
                  <td className="px-3 py-2 font-mono text-foreground">{e.callsign}</td>
                  <td className="px-3 py-2 text-muted-foreground">{ENTITY_LABEL[e.entity]}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => open(e)}
                      aria-label={`Open ${e.name}`}
                      className="text-left text-foreground underline-offset-2 hover:underline"
                    >
                      {e.name}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{e.company_name ?? '—'}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {e.active ? 'Active' : e.entity === 'model' ? 'Retired' : 'Ended'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
